import { useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { cx } from '../../cx'
import styles from './Tabs.module.css'

export interface TabItem {
  key: string
  label: ReactNode
}

export interface TabsProps {
  items: readonly TabItem[]
  value: string
  onChange: (key: string) => void
  'aria-label'?: string
  className?: string
}

export function Tabs({ items, value, onChange, 'aria-label': ariaLabel, className }: TabsProps) {
  const tabRefs = useRef(new Map<string, HTMLButtonElement>())

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (items.length === 0) return
    const currentIndex = items.findIndex((item) => item.key === value)
    if (currentIndex < 0) return

    let nextIndex: number
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % items.length
        break
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + items.length) % items.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = items.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    const next = items[nextIndex]
    if (!next) return
    onChange(next.key)
    tabRefs.current.get(next.key)?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(styles.tablist, className)}
      onKeyDown={onKeyDown}
    >
      {items.map((item) => {
        const selected = item.key === value
        return (
          <button
            key={item.key}
            ref={(element) => {
              if (element) tabRefs.current.set(item.key, element)
              else tabRefs.current.delete(item.key)
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={cx(styles.tab, selected && styles.selected)}
            onClick={() => onChange(item.key)}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}
